import type {
  ConnectorTool,
  ConnectorToolGroup,
  Person,
  PersonOverride,
  PersonOverrideSetting,
  Team,
  TeamApp,
  TeamsOverview,
  TeamUpdate,
  ToolAccess,
  ToolGroup,
} from "@/lib/api";
import { appText } from "@/lib/apps";
import { formatList } from "@/lib/format";
import { i18n } from "@/lib/i18n";
import { nl } from "@/lib/i18n/locales/nl";
import { normalEmail, personLabel } from "@/lib/people";
import { isServed } from "@/lib/tool-groups";

/** The tool everyone always gets: how a person checks their sign-in. Never a unit. */
const WHOAMI = "whoami";

/** The parts of a group, in the order the page shows them: reading first. */
const ACCESS_ORDER: readonly ToolAccess[] = ["read", "write"];

/** A group part as the server names it: "finance:read". */
export function partUnit(group: ToolGroup, access: ToolAccess): string {
  return `${group}:${access}`;
}

/** One group part a team can be given, and a person an exception on. */
export interface GrantPart {
  group: ToolGroup;
  access: ToolAccess;
  unit: string;
  /** The part's served tools in the catalogue's order: each can get its own exception. */
  tools: ConnectorTool[];
  /** True when none of its tools is served yet: every one is coming soon. */
  comingSoon: boolean;
}

/** One row of the access grid: a group with its read and write parts, where it has them. */
export interface GrantRow extends ConnectorToolGroup {
  read: GrantPart | null;
  write: GrantPart | null;
}

/**
 * The tools a team or an exception can name: the catalogue's own. The
 * overview also lists the tools a dashboard role gives (the roadmap's, the
 * developers' issue tools) and the submissions to Velta, in display groups
 * of their own; the role and the sign-in give those, never a team.
 */
export function teamUnitTools<T extends Pick<ConnectorTool, "kind">>(tools: readonly T[]): T[] {
  return tools.filter((tool) => tool.kind !== "role" && tool.kind !== "submission");
}

/**
 * The access grid: every group in the catalogue's order with the parts a team
 * can be given, as the server allows them. A group gets a part where it has a
 * tool with that access, served or coming soon, other than `whoami`; a group
 * with neither (the account group) is left out. Give it the tools a team can
 * name (`teamUnitTools`).
 */
export function grantRows(
  groups: readonly ConnectorToolGroup[],
  tools: readonly ConnectorTool[],
): GrantRow[] {
  return groups
    .map((group) => {
      const part = (access: ToolAccess): GrantPart | null => {
        const partTools = tools.filter(
          (tool) => tool.group === group.id && tool.access === access && tool.name !== WHOAMI,
        );
        if (partTools.length === 0) {
          return null;
        }
        const served = partTools.filter(isServed);
        return {
          group: group.id,
          access,
          unit: partUnit(group.id, access),
          tools: served,
          comingSoon: served.length === 0,
        };
      };
      return { ...group, read: part("read"), write: part("write") };
    })
    .filter((row) => row.read !== null || row.write !== null);
}

/** Every part of the grid, row by row, reading first. */
export function gridParts(rows: readonly GrantRow[]): GrantPart[] {
  return rows.flatMap((row) =>
    ACCESS_ORDER.map((access) => row[access]).filter((part) => part !== null),
  );
}

/** Every part a team can be given now: the grid's units. */
export function grantUnits(rows: readonly GrantRow[]): Set<string> {
  return new Set(gridParts(rows).map((part) => part.unit));
}

/**
 * Grants as the server takes them: each once, sorted, and only parts the grid
 * still offers. A grant on a part the connector no longer has is dropped, so
 * saving clears it rather than failing.
 */
export function knownGrants(grants: readonly string[], units: ReadonlySet<string>): string[] {
  return [...new Set(grants)].filter((unit) => units.has(unit)).sort();
}

/** A team's grants with one part ticked or unticked: each once, sorted, as the server keeps them. */
export function withGrant(grants: readonly string[], unit: string, on: boolean): string[] {
  const next = new Set(grants);
  if (on) {
    next.add(unit);
  } else {
    next.delete(unit);
  }
  return [...next].sort();
}

/** One chip of a team's access: a granted part, with its group's title. */
export interface GrantChip {
  unit: string;
  access: ToolAccess;
  title: string;
}

/** A team's grants as chips, in the grid's order; a grant the grid does not know is left out. */
export function grantChips(grants: readonly string[], rows: readonly GrantRow[]): GrantChip[] {
  const granted = new Set(grants);
  return rows.flatMap((row) =>
    ACCESS_ORDER.flatMap((access) => {
      const part = row[access];
      return part && granted.has(part.unit) ? [{ unit: part.unit, access, title: row.title }] : [];
    }),
  );
}

// ─── Effective access ────────────────────────────────────────────────────

/** Where a person's access to a part or a tool comes from. */
export type AccessSource = "teams" | "default" | "exception" | "always" | "comingSoon";

export interface Decision {
  allowed: boolean;
  source: AccessSource;
}

/** The group parts a person gets before exceptions, and from where. */
export interface BaseAccess {
  units: ReadonlySet<string>;
  /** `teams`: the union of their teams' grants; `default`: in no team, the default team's. */
  source: "teams" | "default";
  /**
   * In the Admins team: every part, every app and the admin-only tools,
   * whatever `units` holds.
   */
  all: boolean;
}

/** A person's exceptions by unit: true allows, false denies. */
export type Exceptions = ReadonlyMap<string, boolean>;

/** Whether a team is the Admins team: fixed, named by the dashboard, with every access. */
export function isAdminsTeam(team: Pick<Team, "system_key"> | null | undefined): boolean {
  return team?.system_key === "admins";
}

/** Every team a person can be in: the Admins team first, then the teams admins made. */
export function namedTeams(overview: Pick<TeamsOverview, "admins_team" | "teams">): Team[] {
  return [overview.admins_team, ...overview.teams];
}

/** A team's name as shown: the default team's and the Admins team's in the dashboard's language. */
export function teamLabel(team: Team): string {
  if (team.is_default) {
    return i18n.t("teams.defaultTeam");
  }
  return isAdminsTeam(team) ? i18n.t("teams.adminsTeam") : team.name;
}

/** Every member of a team: on the Admins team the admins by their role, then its extra members. */
export function allMembers(team: Team): string[] {
  const byRole = team.role_members ?? [];
  return [...byRole, ...team.members.filter((email) => !byRole.includes(email))];
}

/** The Admins team's members, each shown once: by role, stored as extra, or (from older data) both. */
export interface AdminsMembership {
  /** Every dashboard admin, a member by role. */
  byRole: string[];
  /** Admins who are also stored as an extra member: shown once, marked as both. */
  both: Set<string>;
  /** The extra members who are no admin. */
  extras: string[];
}

/**
 * Splits the Admins team's members: an admin is a member by role, so a stored
 * extra member who is also an admin is shown with the admins, marked as both,
 * and the extras list holds only the others.
 */
export function adminsMembership(
  roleMembers: readonly string[],
  members: readonly string[],
): AdminsMembership {
  const byRole = new Set(roleMembers);
  return {
    byRole: [...roleMembers],
    both: new Set(members.filter((email) => byRole.has(email))),
    extras: members.filter((email) => !byRole.has(email)),
  };
}

/**
 * The teams a person is in, by their email (members are stored in lower case),
 * the Admins team's members by role included.
 */
export function teamsOf(email: string, teams: readonly Team[]): Team[] {
  const member = email.toLowerCase();
  return teams.filter((team) => !team.is_default && allMembers(team).includes(member));
}

/**
 * The parts a person gets from their teams: the union of the grants of every
 * team they are in, or the default team's when they are in none. The Admins
 * team gives everything (`all`).
 */
export function baseAccess(email: string, teams: readonly Team[], defaultTeam: Team): BaseAccess {
  const mine = teamsOf(email, teams);
  if (mine.length === 0) {
    return { units: new Set(defaultTeam.grants), source: "default", all: false };
  }
  return {
    units: new Set(mine.flatMap((team) => team.grants)),
    source: "teams",
    all: mine.some((team) => team.all_access === true),
  };
}

/** One person's exceptions out of everyone's. */
export function exceptionsOf(email: string, overrides: readonly PersonOverride[]): Exceptions {
  const person = email.toLowerCase();
  return new Map(
    overrides.filter((o) => o.email.toLowerCase() === person).map((o) => [o.unit, o.allow]),
  );
}

/** A part: its exception when the person has one, else what their teams give. */
export function partDecision(unit: string, base: BaseAccess, exceptions: Exceptions): Decision {
  const exception = exceptions.get(unit);
  if (exception !== undefined) {
    return { allowed: exception, source: "exception" };
  }
  return { allowed: base.all || base.units.has(unit), source: base.source };
}

/**
 * A tool, as the MCP server decides it: `whoami` always; a coming-soon tool
 * never; else the tool's own exception, else its part's exception, else
 * whether the person's teams give the part. An admin-only tool takes the
 * Admins team instead of a part: no other team and no exception that allows
 * its part reach it, an exception that denies its part takes it away. A
 * developer's pause of a tool for everyone applies on top.
 */
export function toolDecision(
  tool: Pick<ConnectorTool, "name" | "group" | "access" | "status"> &
    Partial<Pick<ConnectorTool, "admin_only">>,
  base: BaseAccess,
  exceptions: Exceptions,
): Decision {
  if (tool.name === WHOAMI) {
    return { allowed: true, source: "always" };
  }
  if (!isServed(tool)) {
    return { allowed: false, source: "comingSoon" };
  }
  const exception = exceptions.get(tool.name);
  if (exception !== undefined) {
    return { allowed: exception, source: "exception" };
  }
  const unit = partUnit(tool.group, tool.access);
  if (tool.admin_only) {
    const denied = exceptions.get(unit) === false;
    return { allowed: base.all && !denied, source: denied ? "exception" : base.source };
  }
  return partDecision(unit, base, exceptions);
}

/** How much of a part a person gets: all its tools, some of them, or none. */
export type PartState = "all" | "some" | "none";

/** A person's access to one part. */
export interface PartAccess {
  part: GrantPart;
  /** The part as a whole, before any exception on one of its tools. */
  decision: Decision;
  /** From its served tools; a part with none yet follows its decision. */
  state: PartState;
  /** Whether an exception (on the part or one of its tools) has a say in it. */
  exception: boolean;
}

/** A person's access to every part of the grid, with their exceptions applied. */
export function partAccess(
  rows: readonly GrantRow[],
  base: BaseAccess,
  exceptions: Exceptions,
): PartAccess[] {
  return gridParts(rows).map((part) => {
    const decision = partDecision(part.unit, base, exceptions);
    // An admin-only tool is no part of the part, as on the server.
    const tools = part.tools.filter((tool) => !tool.admin_only);
    const allowed = tools.filter((tool) => toolDecision(tool, base, exceptions).allowed);
    const state: PartState =
      tools.length === 0
        ? decision.allowed
          ? "all"
          : "none"
        : allowed.length === tools.length
          ? "all"
          : allowed.length === 0
            ? "none"
            : "some";
    const exception =
      decision.source === "exception" || part.tools.some((tool) => exceptions.has(tool.name));
    return { part, decision, state, exception };
  });
}

/** One stretch of the access summary: groups with the same access, the same way. */
export interface SummarySegment {
  access: ToolAccess;
  exception: boolean;
  partly: boolean;
  /** Parts whose tools are all coming soon: granted, but nothing to use yet. */
  comingSoon: boolean;
  /** The groups' titles, in the grid's order. */
  groups: string[];
}

/**
 * A person's access in a few stretches: per access (reading first), the groups
 * their teams give in full, then in part, then those an exception decides;
 * each time the parts with tools to use first, then those whose tools are all
 * coming soon. Parts they do not get at all are left out.
 */
export function accessSummary(
  parts: readonly PartAccess[],
  rows: readonly GrantRow[],
): SummarySegment[] {
  const titles = new Map(rows.map((row) => [row.id, row.title]));
  const segments: SummarySegment[] = [];
  for (const access of ACCESS_ORDER) {
    for (const exception of [false, true]) {
      for (const partly of [false, true]) {
        for (const comingSoon of [false, true]) {
          const groups = parts
            .filter(
              (p) =>
                p.part.access === access &&
                p.exception === exception &&
                p.state === (partly ? "some" : "all") &&
                p.part.comingSoon === comingSoon,
            )
            .map((p) => titles.get(p.part.group) ?? p.part.group);
          if (groups.length > 0) {
            segments.push({ access, exception, partly, comingSoon, groups });
          }
        }
      }
    }
  }
  return segments;
}

/**
 * What a person can use in one line: "Alle toegang" for a member of the
 * Admins team without exceptions, else the summary of their parts and apps
 * (`summaryText`).
 */
export function accessLine(
  base: BaseAccess,
  exceptions: Exceptions,
  rows: readonly GrantRow[],
  apps: readonly AppGrant[],
): string {
  if (base.all && exceptions.size === 0) {
    return i18n.t("teams.allAccess");
  }
  const parts = partAccess(rows, base, exceptions);
  return summaryText(accessSummary(parts, rows), openedApps(apps, base, exceptions, rows));
}

/**
 * The summary in one line, in the dashboard's language: "Klanten, Kranen
 * lezen; Financieel lezen (uitzondering); Orders schrijven (binnenkort); app
 * Bezettingsgraad", or "Geen toegang". `apps` are the names of the apps the
 * person may open.
 */
export function summaryText(
  segments: readonly SummarySegment[],
  apps: readonly string[] = [],
): string {
  if (segments.length === 0 && apps.length === 0) {
    return i18n.t("teams.summary.none");
  }
  const appsText = apps.length > 0 ? [i18n.t("teams.apps.summary", { apps: listText(apps) })] : [];
  return [...segments.map(segmentText), ...appsText].join("; ");
}

function segmentText(segment: SummarySegment): string {
  const text = i18n.t(`teams.summary.${segment.access}`, {
    groups: segment.groups.join(", "),
  });
  const notes = [
    segment.comingSoon ? i18n.t("teams.summary.comingSoon") : null,
    segment.partly ? i18n.t("teams.summary.partly") : null,
    segment.exception ? i18n.t("teams.summary.exception") : null,
  ].filter(Boolean);
  return notes.length > 0 ? `${text} (${notes.join(", ")})` : text;
}

// ─── Exceptions, three ways ──────────────────────────────────────────────

/** What a person's exception on a unit says: follow the teams, allow or deny. */
export type ExceptionChoice = "team" | "allow" | "deny";

export const EXCEPTION_CHOICES: readonly ExceptionChoice[] = ["team", "allow", "deny"];

export function exceptionChoice(exceptions: Exceptions, unit: string): ExceptionChoice {
  const exception = exceptions.get(unit);
  return exception === undefined ? "team" : exception ? "allow" : "deny";
}

/** The exceptions with one unit set: "team" removes its exception. */
export function withExceptionChoice(
  exceptions: Exceptions,
  unit: string,
  choice: ExceptionChoice,
): Map<string, boolean> {
  const next = new Map(exceptions);
  if (choice === "team") {
    next.delete(unit);
  } else {
    next.set(unit, choice === "allow");
  }
  return next;
}

/** Every unit an exception can name: the grid's parts, their served tools and the apps. */
export function exceptionUnits(
  rows: readonly GrantRow[],
  apps: readonly AppGrant[] = [],
): Set<string> {
  return new Set([
    ...gridParts(rows).flatMap((part) => [part.unit, ...part.tools.map((tool) => tool.name)]),
    ...apps.map((app) => app.unit),
  ]);
}

/**
 * The exceptions as the server takes them, by unit. One on a unit the
 * connector no longer has is dropped, so saving clears it rather than failing.
 */
export function exceptionSettings(
  exceptions: Exceptions,
  units: ReadonlySet<string>,
): PersonOverrideSetting[] {
  return [...exceptions]
    .filter(([unit]) => units.has(unit))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([unit, allow]) => ({ unit, allow }));
}

/** Whether two sets of exceptions say the same. */
export function sameExceptions(a: Exceptions, b: Exceptions): boolean {
  return a.size === b.size && [...a].every(([unit, allow]) => b.get(unit) === allow);
}

// ─── Apps ────────────────────────────────────────────────────────────────

/**
 * An app a team can be given, with its name and summary in the dashboard's
 * language. A person may open it only with its unit (from a team, or an
 * exception) and every unit it requires; the default team never gives one.
 */
export interface AppGrant {
  slug: string;
  /** "app:bezetting" */
  unit: string;
  title: string;
  summary: string;
  /** The group parts it needs too, such as "finance:read", in the registry's order. */
  requires: string[];
}

/** The apps of the Teams overview, in the dashboard's language. */
export function appGrants(apps: readonly TeamApp[]): AppGrant[] {
  return apps.map((app) => ({ ...app, ...appText(app), requires: [...app.requires] }));
}

/** Every app unit, which a team other than the default team may be given. */
export function appUnits(apps: readonly AppGrant[]): Set<string> {
  return new Set(apps.map((app) => app.unit));
}

/**
 * Every unit a team can hold when it is saved: the grid's parts, and the apps
 * unless it is the default team, which the server refuses an app.
 */
export function teamUnits(
  rows: readonly GrantRow[],
  apps: readonly AppGrant[],
  isDefault: boolean,
): Set<string> {
  return new Set([...grantUnits(rows), ...(isDefault ? [] : appUnits(apps))]);
}

/** The units an app requires that `has` lacks, in the app's order. */
export function missingUnits(
  app: Pick<AppGrant, "requires">,
  has: ReadonlySet<string> | ((unit: string) => boolean),
): string[] {
  const holds = typeof has === "function" ? has : (unit: string) => has.has(unit);
  return app.requires.filter((unit) => !holds(unit));
}

/**
 * A team's grants with one group part ticked or unticked. Unticking a part an
 * app requires also drops that app: the team could no longer open it.
 */
export function withPartGrant(
  grants: readonly string[],
  unit: string,
  on: boolean,
  apps: readonly AppGrant[],
): string[] {
  const next = withGrant(grants, unit, on);
  if (on) {
    return next;
  }
  const held = new Set(next);
  const unmet = new Set(
    apps.filter((app) => missingUnits(app, held).length > 0).map((app) => app.unit),
  );
  return next.filter((grant) => !unmet.has(grant));
}

/**
 * How much a team gives, to sort the teams on: its group parts and its apps;
 * the Admins team every part and every app.
 */
export function teamAccessCount(
  team: Pick<Team, "grants" | "all_access">,
  rows: readonly GrantRow[],
  apps: readonly AppGrant[],
): number {
  if (team.all_access === true) {
    return gridParts(rows).length + apps.length;
  }
  return grantChips(team.grants, rows).length + grantedApps(team.grants, apps).length;
}

/**
 * Whether a team holds one of the chosen groups (any of its parts, by the
 * group's id) or apps (by the app's unit); any team when none is chosen. The
 * Admins team holds every one.
 */
export function teamHolds(
  team: Pick<Team, "grants" | "all_access">,
  chosen: readonly string[],
  rows: readonly GrantRow[],
): boolean {
  if (chosen.length === 0 || team.all_access === true) {
    return true;
  }
  const granted = new Set(team.grants);
  return chosen.some((value) => {
    const row = rows.find((r) => r.id === value);
    if (!row) {
      return granted.has(value);
    }
    return [row.read, row.write].some((part) => part !== null && granted.has(part.unit));
  });
}

/**
 * How much a person gets, to sort the people on: the group parts they get in
 * full or in part and the apps they may open; a member of the Admins team
 * without exceptions every part and every app.
 */
export function personAccessCount(
  base: BaseAccess,
  exceptions: Exceptions,
  rows: readonly GrantRow[],
  apps: readonly AppGrant[],
): number {
  if (base.all && exceptions.size === 0) {
    return gridParts(rows).length + apps.length;
  }
  const parts = partAccess(rows, base, exceptions).filter((part) => part.state !== "none");
  return parts.length + openedApps(apps, base, exceptions, rows).length;
}

/** The apps a team holds, in the registry's order. */
export function grantedApps(grants: readonly string[], apps: readonly AppGrant[]): AppGrant[] {
  const granted = new Set(grants);
  return apps.filter((app) => granted.has(app.unit));
}

/** Words joined the language's way: "Kranen, Klanten en Orders", "Cranes, Clients and Orders". */
export function listText(words: readonly string[]): string {
  return formatList(words);
}

type GroupName = keyof typeof nl.teams.apps.groups;

function isGroupName(group: string): group is GroupName {
  return Object.hasOwn(nl.teams.apps.groups, group);
}

/**
 * Units in words, by access, reading first: "Kranen, Klanten, Orders en
 * Financieel, lezen". A group the dashboard has no short name for takes the
 * grid's title; a unit that is no group part stays as it is.
 */
export function unitsText(units: readonly string[], rows: readonly GrantRow[]): string {
  const titles = new Map(rows.map((row) => [row.id as string, row.title]));
  const groupName = (group: string) =>
    isGroupName(group) ? i18n.t(`teams.apps.groups.${group}`) : (titles.get(group) ?? group);
  const parts = units.map((unit) => {
    const [group = "", access = ""] = unit.split(":");
    return { group, access };
  });
  const segments = ACCESS_ORDER.flatMap((access) => {
    const groups = parts.filter((part) => part.access === access).map((part) => part.group);
    return groups.length > 0
      ? [i18n.t(`teams.apps.units.${access}`, { groups: listText(groups.map(groupName)) })]
      : [];
  });
  const other = parts
    .filter((part) => !(ACCESS_ORDER as readonly string[]).includes(part.access))
    .map((part) => (part.access ? `${part.group}:${part.access}` : part.group));
  return listText([...segments, ...other]);
}

/** Whether a person may open an app, and why not. */
export interface AppDecision {
  allowed: boolean;
  /** The app's own unit: an exception, else their teams (never the default team). */
  unit: Decision;
  /** The required units they lack, after their exceptions. */
  missing: string[];
}

/**
 * An app, as the server decides it: the app's unit (an exception on it wins,
 * else their teams give it; the default team never does) and every unit it
 * requires, each as a group part is decided and with every served tool the
 * part stands for (`rows`) still allowed: a part whose tools exceptions deny
 * one by one no longer counts. Only the tools that count for their part
 * (`counts_for_unit`, not admin-only) stand for it: a tool whose answer shows
 * more than its own part's data, such as the occupancy tools, which can name
 * clients, does not, so an exception on it leaves the part whole. The
 * admins' tool switches do not apply to apps.
 */
export function appDecision(
  app: AppGrant,
  base: BaseAccess,
  exceptions: Exceptions,
  rows: readonly GrantRow[] = [],
): AppDecision {
  const exception = exceptions.get(app.unit);
  const unit: Decision =
    exception !== undefined
      ? { allowed: exception, source: "exception" }
      : {
          allowed: base.all || (base.source === "teams" && base.units.has(app.unit)),
          source: base.source,
        };
  const parts = new Map(gridParts(rows).map((part) => [part.unit, part]));
  const holds = (required: string): boolean =>
    partDecision(required, base, exceptions).allowed &&
    (parts.get(required)?.tools ?? [])
      .filter((tool) => tool.counts_for_unit && !tool.admin_only)
      .every((tool) => toolDecision(tool, base, exceptions).allowed);
  const missing = missingUnits(app, holds);
  return { allowed: unit.allowed && missing.length === 0, unit, missing };
}

/** The names of the apps a person may open. */
export function openedApps(
  apps: readonly AppGrant[],
  base: BaseAccess,
  exceptions: Exceptions,
  rows: readonly GrantRow[] = [],
): string[] {
  return apps
    .filter((app) => appDecision(app, base, exceptions, rows).allowed)
    .map((app) => app.title);
}

// ─── People ──────────────────────────────────────────────────────────────

/** Everyone the page knows, by email. */
export function peopleByEmail(people: readonly Person[]): Map<string, Person> {
  return new Map(people.map((person) => [person.email.toLowerCase(), person]));
}

/** How a member is shown: the name the page knows for them, else their email. */
export function memberLabel(email: string, people: ReadonlyMap<string, Person>): string {
  const person = people.get(email.toLowerCase());
  return person ? personLabel(person) : email;
}

/** The first few members' names for the teams table, and how many more there are. */
export function membersPreview(
  members: readonly string[],
  people: ReadonlyMap<string, Person>,
  shown = 3,
): { names: string[]; more: number } {
  return {
    names: members.slice(0, shown).map((email) => memberLabel(email, people)),
    more: Math.max(0, members.length - shown),
  };
}

// ─── Saving a team ───────────────────────────────────────────────────────

/** What the team editor holds while it is open. */
export interface TeamDraft {
  name: string;
  description: string;
  grants: string[];
  members: string[];
}

export function teamDraft(team: Team | null): TeamDraft {
  return {
    name: team?.name ?? "",
    description: team?.description ?? "",
    grants: [...(team?.grants ?? [])].sort(),
    members: [...(team?.members ?? [])].sort(),
  };
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  const left = [...a].sort();
  const right = [...b].sort();
  return left.length === right.length && left.every((item, i) => item === right[i]);
}

/**
 * The PATCH for what changed in a team: only the fields that differ, and for
 * the default team its grants alone. Null when nothing changed, and always for
 * the Admins team, whose name, description and access the dashboard sets. An
 * emptied description is sent as null, which clears it. Changed grants go
 * without the parts the grid no longer offers (`knownGrants`, from `grantUnits`).
 */
export function teamUpdate(
  team: Team,
  draft: TeamDraft,
  units: ReadonlySet<string>,
): TeamUpdate | null {
  if (isAdminsTeam(team)) {
    return null;
  }
  const change: TeamUpdate = {};
  if (!team.is_default) {
    const name = draft.name.trim();
    if (name !== team.name) {
      change.name = name;
    }
    const description = draft.description.trim();
    if (description !== (team.description ?? "")) {
      change.description = description || null;
    }
  }
  if (!sameList(draft.grants, team.grants)) {
    change.grants = knownGrants(draft.grants, units);
  }
  return Object.keys(change).length > 0 ? change : null;
}

/**
 * The members to PUT when they changed, else null. The default team never has
 * any; on the Admins team they are its extra members, the admins by role apart.
 */
export function membersUpdate(team: Team | null, draft: TeamDraft): string[] | null {
  if (team?.is_default) {
    return null;
  }
  const before = team?.members ?? [];
  return sameList(draft.members, before) ? null : [...draft.members].sort();
}

/** The members with one added (normalised, once) or removed, sorted. */
export function withMember(members: readonly string[], email: string, on: boolean): string[] {
  const next = new Set(members);
  const member = normalEmail(email);
  if (on) {
    next.add(member);
  } else {
    next.delete(member);
  }
  return [...next].sort();
}
